import { useRef, useState } from "react";

function clampPercentage(value: number): number {
  if (Number.isNaN(value)) return 50;
  return Math.min(100, Math.max(0, value));
}

function renderLayer(
  imageSrc: string,
  label: string,
  position: "base" | "overlay",
): React.ReactNode {
  if (imageSrc) {
    return (
      <img
        src={imageSrc}
        alt={label}
        draggable={false}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          userSelect: "none",
          pointerEvents: "none",
          display: "block",
        }}
      />
    );
  }

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: position === "base"
          ? "linear-gradient(135deg, var(--color-muted), color-mix(in srgb, var(--color-muted) 72%, white))"
          : "linear-gradient(135deg, color-mix(in srgb, var(--color-primary) 14%, white), var(--color-muted))",
        color: "var(--color-muted-foreground)",
        fontSize: "0.9rem",
        fontWeight: 500,
        textAlign: "center",
        padding: "1rem",
        boxSizing: "border-box",
      }}
    >
      Choose a {label.toLowerCase()} image
    </div>
  );
}

interface BeforeAfterSliderProps {
  beforeImage: string;
  afterImage: string;
  beforeLabel: string;
  afterLabel: string;
  initialPosition?: number;
}

export function BeforeAfterSlider({
  beforeImage,
  afterImage,
  beforeLabel,
  afterLabel,
  initialPosition = 50,
}: BeforeAfterSliderProps): React.ReactNode {
  const [position, setPosition] = useState(() => clampPercentage(initialPosition));
  const rootRef = useRef<HTMLDivElement | null>(null);
  const activePointerIdRef = useRef<number | null>(null);

  const updatePositionFromClientX = (clientX: number) => {
    const rect = rootRef.current?.getBoundingClientRect();
    if (!rect || rect.width <= 0) return;
    const next = ((clientX - rect.left) / rect.width) * 100;
    setPosition(clampPercentage(next));
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 && event.pointerType !== "touch" && event.pointerType !== "pen") {
      return;
    }

    event.preventDefault();
    activePointerIdRef.current = event.pointerId;
    event.currentTarget.setPointerCapture(event.pointerId);
    updatePositionFromClientX(event.clientX);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (activePointerIdRef.current !== event.pointerId) {
      return;
    }

    event.preventDefault();
    updatePositionFromClientX(event.clientX);
  };

  const releasePointer = (event: React.PointerEvent<HTMLDivElement>) => {
    if (activePointerIdRef.current !== event.pointerId) {
      return;
    }

    activePointerIdRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  return (
    <div style={{ maxWidth: "720px", margin: "0 auto", display: "grid", gap: "0.9rem" }}>
      <div
        ref={rootRef}
        data-before-after-slider=""
        data-before-after-position={String(Math.round(position))}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={releasePointer}
        onPointerCancel={releasePointer}
        style={{
          position: "relative",
          aspectRatio: "4 / 3",
          overflow: "hidden",
          borderRadius: "16px",
          border: "1px solid var(--color-border)",
          background: "var(--color-card)",
          boxShadow: "0 16px 40px rgba(15, 23, 42, 0.12)",
          touchAction: "none",
          userSelect: "none",
          cursor: "ew-resize",
        }}
      >
        <div style={{ position: "absolute", inset: 0 }}>
          {renderLayer(beforeImage, beforeLabel, "base")}
        </div>
        <div
          data-before-after-overlay=""
          style={{
            position: "absolute",
            inset: 0,
            clipPath: `inset(0 ${100 - position}% 0 0)`,
          }}
        >
          {renderLayer(afterImage, afterLabel, "overlay")}
        </div>

        <span
          style={{
            position: "absolute",
            top: "0.9rem",
            left: "0.9rem",
            zIndex: 2,
            padding: "0.35rem 0.7rem",
            borderRadius: "999px",
            background: "rgba(15, 23, 42, 0.72)",
            color: "white",
            fontSize: "0.78rem",
            fontWeight: 700,
            letterSpacing: "0.02em",
          }}
        >
          {beforeLabel}
        </span>
        <span
          style={{
            position: "absolute",
            top: "0.9rem",
            right: "0.9rem",
            zIndex: 2,
            padding: "0.35rem 0.7rem",
            borderRadius: "999px",
            background: "rgba(255, 255, 255, 0.82)",
            color: "var(--color-foreground)",
            fontSize: "0.78rem",
            fontWeight: 700,
            letterSpacing: "0.02em",
          }}
        >
          {afterLabel}
        </span>

        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: `${position}%`,
            width: "0",
            zIndex: 2,
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: "-1px",
              width: "2px",
              background: "rgba(255, 255, 255, 0.96)",
              boxShadow: "0 0 0 1px rgba(15, 23, 42, 0.16)",
            }}
          />
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "0",
              transform: "translate(-50%, -50%)",
              width: "3rem",
              height: "3rem",
              borderRadius: "999px",
              border: "2px solid rgba(15, 23, 42, 0.14)",
              background: "rgba(255, 255, 255, 0.94)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--color-foreground)",
              fontSize: "1rem",
              fontWeight: 800,
              boxShadow: "0 14px 30px rgba(15, 23, 42, 0.18)",
            }}
          >
            ↔
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gap: "0.45rem" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: "0.75rem",
            color: "var(--color-muted-foreground)",
          }}
        >
          <span>{beforeLabel}</span>
          <span>{Math.round(position)}%</span>
          <span>{afterLabel}</span>
        </div>
        <input
          type="range"
          min={0}
          max={100}
          step={1}
          aria-label="Before and after reveal position"
          value={Math.round(position)}
          onChange={(event) => setPosition(clampPercentage(Number(event.currentTarget.value)))}
          style={{ width: "100%" }}
        />
      </div>
    </div>
  );
}
